import React, { useState } from "react";

const FileUpload = ({
  onFilesChange,
  files = [],
  maxFiles = 10,
  maxSizePerFile = 10 * 1024 * 1024,
  onUploadingChange,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Allowed file types
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "text/plain",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  const allowedExtensions = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".gif", ".txt", ".xls", ".xlsx"];

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Validate file
  const validateFile = (file) => {
    const errors = [];

    // Check file size
    if (file.size > maxSizePerFile) {
      errors.push(`File size exceeds ${formatFileSize(maxSizePerFile)} limit`);
    }

    // Check file type
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      errors.push("File type not supported");
    }

    return errors;
  };

  // Upload file to Cloudinary
  const uploadToCloudinary = async (file, uniqueFolder) => {
    const url = "https://api.cloudinary.com/v1_1/dqbckxsgm/upload";
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "rcoy0hs1");
    formData.append("api_key", "789145474162712");
    formData.append("folder", `draw_requests/${uniqueFolder}`);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.secure_url) {
        return {
          url: data.secure_url,
          public_id: data.public_id,
          original_filename: data.original_filename,
          bytes: data.bytes,
          format: data.format,
        };
      } else {
        throw new Error(data.error?.message || "Upload failed");
      }
    } catch (error) {
      throw error;
    }
  };

  // Process files and upload to Cloudinary
  const processFiles = async (fileList) => {
    const errors = [];

    // Check total file limit
    if (files.length + fileList.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed`);
      setUploadErrors(errors);
      return;
    }

    setUploading(true);
    if (onUploadingChange) onUploadingChange(true);
    setUploadErrors([]);

    // Generate unique timestamp folder for this upload session
    const timestamp = Date.now();
    const uniqueFolder = `${timestamp}`;

    const validFiles = [];

    // Validate all files first
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const fileErrors = validateFile(file);

      if (fileErrors.length > 0) {
        errors.push(`${file.name}: ${fileErrors.join(", ")}`);
        continue;
      }

      validFiles.push(file);
    }

    // If there are validation errors, show them and stop
    if (errors.length > 0) {
      setUploadErrors(errors);
      setUploading(false);
      if (onUploadingChange) onUploadingChange(false);
      return;
    }

    // Upload all valid files
    const uploadResults = [];

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      try {
        const cloudinaryData = await uploadToCloudinary(file, uniqueFolder);
        uploadResults.push({
          id: Date.now() + i,
          name: file.name,
          size: file.size,
          type: file.type,
          status: "uploaded",
          cloudinaryUrl: cloudinaryData.url,
          publicId: cloudinaryData.public_id,
          originalFilename: cloudinaryData.original_filename,
          uniqueFolder: uniqueFolder,
        });
      } catch (error) {
        errors.push(`${file.name}: Upload failed - ${error.message}`);
      }
    }

    if (errors.length > 0) {
      setUploadErrors(errors);
    }

    if (uploadResults.length > 0) {
      onFilesChange([...files, ...uploadResults]);
    }

    setUploading(false);
    if (onUploadingChange) onUploadingChange(false);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
    e.target.value = ""; // Clear input
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  };

  // Remove file
  const removeFile = (fileId) => {
    const updatedFiles = files.filter((file) => file.id !== fileId);
    onFilesChange(updatedFiles);
  };

  // Clear all errors
  const clearErrors = () => {
    setUploadErrors([]);
  };

  return (
    <div className="w-full">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragOver
            ? "border-blue-400 bg-blue-50"
            : uploading
            ? "border-yellow-400 bg-yellow-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          accept={allowedExtensions.join(",")}
          disabled={uploading}
        />

        {uploading ? (
          // Uploading State
          <div className="py-8">
            <div className="animate-spin mx-auto h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            <div className="mt-4">
              <p className="text-lg font-medium text-blue-900">Uploading files...</p>
              <p className="text-sm text-blue-600 mt-1">
                Please wait while we upload your documents to our secure server
              </p>
            </div>
          </div>
        ) : files.length === 0 ? (
          // Zero State
          <div className="py-6">
            <div className="mx-auto h-12 w-12 text-gray-400 text-4xl">
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="mx-auto size-12 text-gray-400 dark:text-gray-500"
              >
                <path
                  d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="mt-4">
              <p className="text-lg font-medium text-gray-900">Upload documents</p>
              <p className="text-sm text-gray-600 mt-1">Drag and drop files here, or click to browse</p>
              <p className="text-xs text-gray-500 mt-2">
                PDF, DOC, DOCX, JPG, PNG, GIF, TXT, XLS, XLSX up to {formatFileSize(maxSizePerFile)}
              </p>
            </div>
          </div>
        ) : (
          // Files Uploaded State
          <div className="py-4">
            <div className="mx-auto h-8 w-8 text-green-500 text-2xl">✅</div>
            <p className="text-sm text-gray-600 mt-2">
              {files.length} file{files.length !== 1 ? "s" : ""} uploaded. Click or drag to add more.
            </p>
          </div>
        )}
      </div>

      {/* Error Messages */}
      {uploadErrors.length > 0 && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start">
            <div className="h-5 w-5 text-red-400 mt-0.5 text-lg">⚠️</div>
            <div className="ml-2 flex-1">
              <h3 className="text-sm font-medium text-red-800">Upload Errors:</h3>
              <ul className="mt-1 text-sm text-red-700">
                {uploadErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
              <button onClick={clearErrors} className="mt-2 text-xs text-red-600 hover:text-red-800 underline">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Uploaded Documents ({files.length}/{maxFiles})
          </h4>
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md"
              >
                <div className="flex items-center flex-1 min-w-0">
                  {/* File Icon */}
                  <div className="flex-shrink-0 text-2xl">
                    {file.type.startsWith("image/") ? "🖼️" : file.type === "application/pdf" ? "📄" : "📄"}
                  </div>

                  {/* File Info */}
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    {file.cloudinaryUrl && <p className="text-xs text-green-600">✓ Uploaded to secure server</p>}
                  </div>

                  {/* Status */}
                  <div className="ml-3 flex-shrink-0">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        file.status === "uploaded" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {file.status === "uploaded" ? "Uploaded" : "Processing"}
                    </span>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeFile(file.id)}
                  className="ml-3 flex-shrink-0 p-1 text-gray-400 hover:text-red-600 transition-colors text-lg"
                  title="Remove file"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Limits Info */}
      <div className="mt-3 text-xs text-gray-500 space-y-2">
        <p>• Maximum {maxFiles} files</p>
        <p>• Maximum {formatFileSize(maxSizePerFile)} per file</p>
      </div>
    </div>
  );
};

export default FileUpload;
